import React, { FC, useEffect, useState } from 'react';
import MySavedDeal from '@components/DealPreview/components/MySavedDeal';
import { DealPreview } from '@components/DealPreview/DealPreview';
import useCustomRouter from '@hooks/useCustomRouter';
import useTranslations from '@hooks/useTranslations';
import { getRedirectDestinationOnActivateDeal, constructPsaCheckoutUrl } from '@utils/Deal.utils';
import CarDetailsDuck from '../../../redux/carDetails/carDetails.duck';
import { connect, useDispatch, useSelector } from 'react-redux';
import UserDuck from '../../../redux/user/user.duck';
import { MySavedDealButtonType } from '../types';
import Paginator, { IPaginatorPage } from '@components/Paginator';
import routes from '../../../constants/routes';
import { MyAccountSidebar } from '@components/MyAccountSidebar';
import { useOffersRedirect } from '@hooks/useOffersRedirect';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitchApp';
import { useUserDuck } from '@hooks/useUserDuck';
import { IMySavedDeal, MySavedDealJourneyType, IduckState } from 'src/redux/user/user.duck.interface';
import { IConfigurableCar, IDeal } from 'src/interfaces/Car';
import { useExternalCheckout } from '@hooks/useExternalCheckout';
import { useRouter } from 'next/router';
import { NETWORK_STATUS_DONE, NETWORK_STATUS_START, Redux } from 'src/redux/redux.interface';
import { useFeatureSwitch } from '@hooks/useFeatureSwitch';

interface IMySavesProps {
    mySaves: IMySavedDeal[];
    actionSetActiveDeal: (dealId: string) => void;
    activateDealDTO: IduckState['setActiveDeal'];
    actionDeleteDeal: (dealId: string) => void;
    deleteDealDTO: IduckState['deleteDeal'];
    totalCount: number;
    page: number;
    perPage: number;
    showPaginator: boolean;
}

const MySavesController: FC<IMySavesProps> = ({
    mySaves,
    actionSetActiveDeal,
    activateDealDTO,
    actionDeleteDeal,
    deleteDealDTO,
    totalCount,
    page,
    perPage,
    showPaginator,
}: IMySavesProps) => {
    const router = useCustomRouter();
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { redirectToOffers } = useOffersRedirect();
    const [shouldGetOffer, setShouldGetOffer] = useState(false);
    const [scrolled, setScrolled] = useState<boolean>(false);
    const { query } = useRouter();
    const isFinanceJourneyPSACheckoutRedirectAllowed = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_BRAND_RECOMMENDS_FINANCE_JOURNEY_PSA_CHECKOUT
    );
    const isSOLEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SOL_ENABLED);
    const currentDeal = useSelector((state) => CarDetailsDuck.getCurrentDeal(state));

    const handlePageClick = ({ selected }: IPaginatorPage) => {
        window.scrollTo(0, 0);
        const newPage: number = selected + 1;
        const queryPage = newPage === 1 ? '' : '?page=' + newPage;
        router.push(routes.MY_SAVES + queryPage);
    };

    const { isUserLoggedIn } = useUserDuck();
    const { checkoutUrl } = useExternalCheckout();
    const { isStockSummaryEnabled, isMTOSummaryEnabled } = useFeatureSwitch();

    useEffect(() => {
        const { skipToContent } = query;
        if (skipToContent === 'true') {
            const skipContent = document.getElementById('skip-to-content');
            if (skipContent) {
                const y = skipContent?.getBoundingClientRect()?.top + window.pageYOffset - 50;
                window.scrollTo({ top: y, behavior: 'smooth' });
                setScrolled(true);
            }
        }
    }, [mySaves]);

    useEffect(() => {
        if (scrolled) {
            router.push(
                {
                    pathname: routes.MY_SAVES,
                    query: { skipToContent: false },
                },
                { shallow: true }
            );
        }
    }, [scrolled]);

    useEffect(() => {
        if (activateDealDTO.status === NETWORK_STATUS_DONE) {
            const flattenedSaves = mySaves.reduce((sum, item) => {
                return [...sum, item, ...item.children];
            }, []);

            const deal: IMySavedDeal = flattenedSaves.find((save) => save?.token === activateDealDTO.id);

            // if the useExternalCheckout key does not exists then the deal was made before Disengage Drivvn checkout bundle of FS
            // and then only cash deals and finance BR should redirect there
            const legacyExternalRedirect =
                !('useExternalCheckout' in deal.extraFields) &&
                (deal.journeyType === MySavedDealJourneyType.BUY_ONLINE_CASH ||
                    isFinanceJourneyPSACheckoutRedirectAllowed);

            if (deal !== undefined) {
                if (currentDeal?.token !== deal.token) {
                    dispatch(CarDetailsDuck.setCurrentDeal(deal as unknown as IDeal<IConfigurableCar>));
                }
                if (shouldGetOffer) {
                    redirectToOffers();
                } else if (deal.mopId && (deal.extraFields.useExternalCheckout || legacyExternalRedirect)) {
                    window.location.href = constructPsaCheckoutUrl(checkoutUrl, deal.mopId, isUserLoggedIn);
                } else {
                    const isSummaryEnabled = deal.fullProductConfiguration.stock
                        ? isStockSummaryEnabled
                        : isMTOSummaryEnabled;
                    const redirectRoute = getRedirectDestinationOnActivateDeal(deal, isSummaryEnabled);
                    if (redirectRoute) router.push(redirectRoute);
                }
            }
        }
    }, [activateDealDTO.status, mySaves, shouldGetOffer, isFinanceJourneyPSACheckoutRedirectAllowed, checkoutUrl]);

    const renderMySavesDealList = () => {
        return mySaves.map((mySavedDeal: IMySavedDeal, key) => {
            const handleActivateDeal = () => {
                actionSetActiveDeal(mySavedDeal.token);
            };
            const handleDeleteDeal = () => {
                actionDeleteDeal(mySavedDeal.token);
            };
            const handleGetPersonalisedOffer = () => {
                setShouldGetOffer(true);
                actionSetActiveDeal(mySavedDeal.token);
            };
            const btnActivateStatus =
                activateDealDTO.id === mySavedDeal.token
                    ? MySavedDealButtonType.PROGRESS
                    : activateDealDTO.status === NETWORK_STATUS_START || deleteDealDTO.status === NETWORK_STATUS_START
                    ? MySavedDealButtonType.DISABLED
                    : MySavedDealButtonType.ENABLED;
            const btnDeleteStatus =
                deleteDealDTO.id === mySavedDeal.token
                    ? MySavedDealButtonType.PROGRESS
                    : activateDealDTO.status === NETWORK_STATUS_START || deleteDealDTO.status === NETWORK_STATUS_START
                    ? MySavedDealButtonType.DISABLED
                    : MySavedDealButtonType.ENABLED;

            if (
                isSOLEnabled &&
                !mySavedDeal.fullProductConfiguration.pricesV2 &&
                !mySavedDeal.fullProductConfiguration.extraFields?.pricesV2
            ) {
                return null;
            }
            return (
                <DealPreview key={mySavedDeal.token}>
                    <MySavedDeal
                        id={key === 0 && `skip-to-content`}
                        mySavedDeal={mySavedDeal}
                        handleActivateDeal={handleActivateDeal}
                        handleDeleteDeal={handleDeleteDeal}
                        handleGetPersonalisedOffer={handleGetPersonalisedOffer}
                        btnActivateType={btnActivateStatus}
                        btnDeleteType={btnDeleteStatus}
                        btnGetPersonalisedOfferType={btnActivateStatus}
                    />
                </DealPreview>
            );
        });
    };

    if (mySaves.length === 0) {
        return (
            <div className="nothing-found">
                <h1>{t('myaccount.deal.nothingFound')}</h1>
            </div>
        );
    }
    return (
        <div className="my-account-box">
            <div className="my-saves">
                {renderMySavesDealList()}
                {showPaginator && totalCount > perPage && (
                    <Paginator page={page} handlePageClick={handlePageClick} total={totalCount} perPage={perPage} />
                )}
            </div>
            <div className="my-account-menu">
                <MyAccountSidebar />
            </div>
        </div>
    );
};

export { MySavesController };

const mapStateToProps = (state: Redux) => {
    const userState = UserDuck.getOwnState(state);

    return {
        activateDealDTO: userState.setActiveDeal,
        deleteDealDTO: userState.deleteDeal,
        totalCount: userState.myAccountSummary.mySaves.total,
        page: userState.mySaves.page,
        perPage: userState.mySaves.perPage,
    };
};

const mapDispatchToProps = {
    actionSetActiveDeal: UserDuck.actionSetActiveDeal,
    actionDeleteDeal: UserDuck.actionDeleteMySavedDeal,
};

export default connect(mapStateToProps, mapDispatchToProps)(MySavesController);
